/**
 * Wave 29 Chunk 7 — /team/leave manager page.
 *
 * Two views, depending on role:
 *  - HR (owner/admin): full org list with status tabs (pending/approved/...)
 *    and approve/deny actions per row.
 *  - Staff: their own application history + "Apply for leave" link to
 *    the full-page form at /team/leave/new (no modal — DESIGN_CONTRACT Rule 0).
 *
 * SimplePay auto-push fires on approval (best-effort, will surface error
 * in the row if it fails).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarDays, ChevronLeft, Plus, CheckCircle2,
  Ban, Clock,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";

const TABS = [
  { v: "pending", l: "Pending" },
  { v: "approved", l: "Approved" },
  { v: "denied", l: "Denied" },
  { v: "cancelled", l: "Cancelled" },
];

const STATUS_STYLES = {
  pending: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  denied: "bg-red-500/15 text-red-300 border-red-500/40",
  cancelled: "bg-white/[0.06] text-[#647087] border-white/15",
};

export default function LeaveManager() {
  const { user } = useAuth();
  const isHr = user && ["owner", "admin"].includes(user.role);
  const [items, setItems] = useState([]);
  const [balance, setBalance] = useState(null);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      if (isHr) {
        const { data } = await api.get("/team/leave/applications", { params: { status: tab } });
        setItems(data.items || []);
        const { data: b } = await api.get("/team/leave/balance/me").catch(() => ({ data: null }));
        setBalance(b);
      } else {
        const { data } = await api.get("/team/leave/applications/mine");
        setItems((data.items || []).filter((i) => i.status === tab));
        setBalance(data.balance);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, isHr]);

  const approve = async (lv) => {
    try {
      await api.post(`/team/leave/applications/${lv.id}/approve`);
      toast.success(`Approved. ${lv.days} days deducted.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed."); }
  };
  const deny = async (lv) => {
    const reason = window.prompt(`Deny ${lv.user_name}'s ${lv.kind} leave — reason:`);
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post(`/team/leave/applications/${lv.id}/deny`, { reason: reason.trim() });
      toast.success("Denied.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Deny failed."); }
  };
  const cancel = async (lv) => {
    if (!window.confirm(`Cancel your ${lv.kind} leave from ${lv.start_date}?`)) return;
    try {
      await api.post(`/team/leave/applications/${lv.id}/cancel`);
      toast.success("Cancelled.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed."); }
  };

  if (!user) return null;
  return (
    <div className="space-y-4" data-testid="leave-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to={isHr ? "/team" : "/me"} className="hv-btn hv-btn-ghost text-xs">
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </Link>
        <h1 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-[#1FB6C1]" /> Leave
        </h1>
        <Link to="/team/leave/new" className="hv-btn hv-btn-primary text-xs" data-testid="leave-apply-btn">
          <Plus className="w-3.5 h-3.5" /> Apply
        </Link>
      </div>

      {balance && (
        <div className="hv-card p-4 flex items-center gap-4" data-testid="leave-balance-card">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Annual allowance</div>
            <div className="font-display text-2xl font-bold tabular-nums">{balance.annual_allowance} d</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Used (12m)</div>
            <div className="font-display text-2xl font-bold tabular-nums text-amber-300">{balance.annual_used_12m} d</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Remaining</div>
            <div className="font-display text-2xl font-bold tabular-nums text-emerald-300" data-testid="leave-remaining">{balance.annual_remaining} d</div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-1 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t.v}
            onClick={() => setTab(t.v)}
            className={`text-[11px] px-2.5 py-1 rounded-full border ${
              tab === t.v ? "bg-[#1FB6C1] text-[#0a0b0e] border-[#1FB6C1]" : "border-white/15 text-[#a0aabf]"
            }`}
            data-testid={`leave-tab-${t.v}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="h-32 hv-card animate-pulse" />
      ) : items.length === 0 ? (
        <div className="hv-card p-8 text-center text-sm text-[#647087]">No {tab} leave.</div>
      ) : (
        <div className="space-y-2" data-testid="leave-list">
          {items.map((lv) => (
            <div key={lv.id} className="hv-card p-3 flex items-start gap-3 flex-wrap" data-testid={`leave-${lv.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display font-semibold text-sm">{lv.user_name}</h3>
                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/[0.06] text-[#a0aabf]">{lv.kind}</span>
                  <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${STATUS_STYLES[lv.status] || ""}`}>{lv.status}</span>
                  {lv.simplepay_pushed_at && (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/40" title={lv.simplepay_pushed_at}>
                      pushed to SimplePay
                    </span>
                  )}
                  {lv.simplepay_error && (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/40" title={lv.simplepay_error}>
                      SP push failed
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-[#a0aabf] mt-1">
                  <Clock className="w-3 h-3 inline mr-0.5" />
                  {lv.start_date} → {lv.end_date} · <b className="text-white">{lv.days} day{lv.days === 1 ? "" : "s"}</b>
                </div>
                {lv.reason && <div className="text-[11px] text-[#cfd6e3] mt-1">{lv.reason}</div>}
                {lv.denied_reason && <div className="text-[11px] text-red-300 mt-1">Denied: {lv.denied_reason}</div>}
              </div>
              {lv.status === "pending" && (
                <div className="flex items-center gap-1 shrink-0">
                  {isHr && (
                    <>
                      <button onClick={() => approve(lv)} className="hv-btn hv-btn-primary text-xs" data-testid={`leave-approve-${lv.id}`}>
                        <CheckCircle2 className="w-3 h-3" /> Approve
                      </button>
                      <button onClick={() => deny(lv)} className="hv-btn hv-btn-ghost text-xs text-red-300" data-testid={`leave-deny-${lv.id}`}>
                        <Ban className="w-3 h-3" /> Deny
                      </button>
                    </>
                  )}
                  {(lv.user_id === user.id || isHr) && (
                    <button onClick={() => cancel(lv)} className="hv-btn hv-btn-ghost text-xs text-[#647087]" data-testid={`leave-cancel-${lv.id}`}>
                      Cancel
                    </button>
                  )}
                </div>
              )}
              {lv.status === "approved" && (lv.user_id === user.id || isHr) && (
                <button onClick={() => cancel(lv)} className="hv-btn hv-btn-ghost text-xs text-[#647087]" data-testid={`leave-cancel-${lv.id}`}>
                  Cancel
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
